/**
 * Tray configuration definitions
 *
 * Each tray type declares its button properties, badge renderer, and content renderer.
 * This keeps all tray-specific logic in one place for easy extension.
 */

import { FolderOpen, GitCommitHorizontal, GitFork, NotebookPen, Search, TerminalSquare } from "lucide-react"
import { getTaskPtyId } from "../../nestAPIs/pty"
import type { TrayManager } from "../../store/managers/TrayManager"
import type { TrayConfig } from "../../modules/types"
import { GitLogTray } from "../GitLogTray"
import { SearchTray } from "../SearchTray"
import { TaskSessionsPanel } from "../sessions/TaskSessionsPanel"
import { Terminal } from "../Terminal"
import { FilesTrayContent } from "./FilesTrayContent"
import { gitLogButtonLabel, GitLogSummary } from "./GitChangeIndicator"
import { ScratchpadTrayContent } from "./ScratchpadTrayContent"

function NoEnvironment() {
    return <div className="flex items-center justify-center h-full text-muted text-sm">Environment not available</div>
}

function isGitBackedTrayVisible(tray: TrayManager): boolean {
    return tray.store.repos.getGitInfoSync(tray.taskModel.repoId)?.isGitRepo === true
}

export const CORE_TRAY_CONFIGS: readonly TrayConfig[] = [
    {
        id: "gitlog",
        label: "Git Log",
        compactLabel: "Git changes",
        icon: GitCommitHorizontal,
        isVisible: isGitBackedTrayVisible,
        onOpen: (tray) => {
            void tray.taskModel.refreshDirectoryView()
            tray.taskModel.changes.initializeForTray()
        },
        renderButtonStatus: (tray) => <GitLogSummary gitStatus={tray.taskModel.displayGitStatus} taskCommitCount={tray.taskModel.taskCommitCount} />,
        getButtonLabel: (tray) => gitLogButtonLabel(tray.taskModel.displayGitStatus, tray.taskModel.taskCommitCount),
        renderContent: (tray, { compact }) => {
            const directory = tray.taskModel.getDirectory("working")
            if (!directory) {
                return <NoEnvironment />
            }
            return (
                <GitLogTray
                    changesManager={tray.taskModel.changes}
                    taskId={tray.taskId}
                    workDir={directory.path}
                    workspaceId={directory.context.workspaceId}
                    workspaceRoot={directory.context.workspaceRoot}
                    {...(directory.context.worktreeId ? { worktreeId: directory.context.worktreeId } : {})}
                    currentBranch={tray.taskModel.gitStatus?.branch ?? null}
                    taskStartCommit={tray.taskModel.directoryView?.diffBaseCommit}
                    className="h-full"
                    compact={compact}
                />
            )
        },
    },
    {
        id: "files",
        label: "Files",
        icon: FolderOpen,
        onOpen: (tray) => tray.taskModel.fileBrowser.refreshTree(),
        renderContent: (tray, { compact }) => (
            <FilesTrayContent fileBrowser={tray.taskModel.fileBrowser} taskId={tray.taskId} onClose={() => tray.close()} compact={compact} />
        ),
    },
    {
        id: "sessions",
        label: "Sessions",
        icon: GitFork,
        isVisible: (tray) => tray.taskModel.sessionDebugInfo.some((session) => session.role === "auxiliary" || Boolean(session.parentSessionId)),
        renderContent: (tray) => (
            <TaskSessionsPanel
                taskId={tray.taskId}
                sessions={tray.taskModel.sessionDebugInfo}
                loading={tray.taskModel.taskSessionManifestLoading}
                error={tray.taskModel.taskSessionManifestError}
                onRefresh={async () => {
                    await Promise.all([tray.taskModel.loadTaskSessionManifest(true), tray.taskModel.loadNestUsage()])
                }}
            />
        ),
    },
    {
        id: "search",
        label: "Search",
        icon: Search,
        renderContent: (tray, { compact }) => (
            <SearchTray contentSearch={tray.taskModel.contentSearch} taskId={tray.taskId} onEscapeClose={() => tray.close()} compact={compact} />
        ),
    },
    {
        id: "terminal",
        label: "Terminal",
        icon: TerminalSquare,
        onClose: (tray) => void tray.taskModel.refreshDirectoryView(),
        renderContent: (tray, { compact }) => {
            const directory = tray.taskModel.getDirectory("working")
            if (!directory) {
                return <NoEnvironment />
            }
            return (
                <Terminal
                    ptyId={getTaskPtyId(tray.taskId)}
                    cwd={directory.path}
                    context={directory.context}
                    className="h-full"
                    onClose={() => tray.close()}
                    compact={compact}
                />
            )
        },
    },
    {
        id: "scratchpad",
        label: "Scratch Pad",
        compactLabel: "Notes",
        icon: NotebookPen,
        renderContent: (tray, { compact }) => {
            const repo = tray.store.repos.getRepo(tray.workspaceId)
            return <ScratchpadTrayContent workspaceId={tray.workspaceId} repoPath={repo?.path ?? null} compact={compact} />
        },
    },
]
